import React, { useEffect, useMemo } from 'react';
import { Table, Button, Space, Popconfirm, message, Input, Tag, Modal, Grid, Card } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchShops, deleteShop, updateShop } from '../store/shopsSlice';
import { fetchBDEs } from '../store/bdeSlice';
import { EditOutlined, DeleteOutlined, ShopOutlined, FileExcelOutlined } from '@ant-design/icons';
import { API_URL } from '../utils/api';
import { exportToExcel } from '../utils/exportUtils';

const Shops = () => {
  const { shops, loading } = useSelector((state) => state.shops);
  const { orders } = useSelector((state) => state.orders);
  const { users: bdes } = useSelector((state) => state.bde);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');

  useEffect(() => {
    dispatch(fetchShops());
    dispatch(fetchBDEs());
  }, [dispatch]);

  const [isRejectModalVisible, setIsRejectModalVisible] = React.useState(false);
  const [currentShop, setCurrentShop] = React.useState(null);
  const [rejectReason, setRejectReason] = React.useState('');
  const [updateLoading, setUpdateLoading] = React.useState(false);

  const handleDelete = (id) => {
    dispatch(deleteShop(id))
      .unwrap()
      .then(() => message.success('Shop deleted'))
      .catch((err) => message.error('Failed to delete shop: ' + err));
  };

  const handleVerify = (shop) => {
    // Optimistically update or wait?
    // Shops list updates via SSE or re-fetch?
    // Just dispatch update.
    const formData = new FormData();
    formData.append('status', 'Verified');
    // Must append other required fields? 
    // updateShop uses `findOneAndUpdate` which does partial updates if we send only fields provided?
    // Checking `shopRoutes.js`: `const updateData = { ...req.body }; ... findOneAndUpdate(..., updateData, ...)`.
    // Yes, it does partial updates. But wait, `req.file` logic implies multipart.
    // So formData is correct.

    dispatch(updateShop({ id: shop.id, data: formData })) // updateShop action handles FormData
      .unwrap()
      .then(() => message.success('Shop verified successfully'))
      .catch(err => message.error('Verification failed: ' + err));
  };

  const openRejectModal = (shop) => {
    setCurrentShop(shop);
    setRejectReason('');
    setIsRejectModalVisible(true);
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      message.error('Please enter a reason for rejection');
      return;
    }
    setUpdateLoading(true);
    const formData = new FormData();
    formData.append('status', 'Rejected');
    formData.append('rejectionReason', rejectReason);

    dispatch(updateShop({ id: currentShop.id, data: formData }))
      .unwrap()
      .then(() => {
        message.success('Shop rejected');
        setIsRejectModalVisible(false);
      })
      .catch(err => message.error('Rejection failed: ' + err))
      .finally(() => setUpdateLoading(false));
  };

  const filteredShops = useMemo(() => {
    return shops.filter((s) => {
      const q = query.toLowerCase().trim();
      if (!q) return true;
      return (
        (s.name && s.name.toLowerCase().includes(q)) ||
        (s.ownerName && s.ownerName.toLowerCase().includes(q)) ||
        (s.pincode && s.pincode.includes(q))
      );
    }).sort((a, b) => (a.pincode || '').localeCompare(b.pincode || ''));
  }, [shops, query]);

  const handleExport = () => {
    if (!shops || shops.length === 0) {
      message.warning('No shops to export');
      return;
    }

    const dataToExport = shops.map(s => {
      const bde = bdes.find(u => u.id === s.bdeId);
      return {
        ID: s.id,
        'Shop Name': s.name,
        'Owner Name': s.ownerName,
        Mobile: s.contactNumber,
        Pincode: s.pincode,
        Address: s.address,
        Status: s.status,
        'Added By': bde ? bde.name : (s.bdeId || 'Admin'),
        'BDE Mobile': bde ? bde.mobile : 'N/A'
      };
    });

    exportToExcel(dataToExport, 'Shops_List');
    message.success('Shops exported successfully');
  };

  // Calculate rowSpan for Pincode grouping
  const getRowSpan = (value, index, data) => {
    const obj = {
      children: value,
      props: {},
    };
    if (index === 0 || data[index - 1].pincode !== value) {
      let count = 0;
      for (let i = index; i < data.length; i++) {
        if (data[i].pincode === value) {
          count++;
        } else {
          break;
        }
      }
      obj.props.rowSpan = count;
    } else {
      obj.props.rowSpan = 0;
    }
    return obj;
  };

  const screens = Grid.useBreakpoint();

  const columns = [
    {
      title: 'Pincode',
      dataIndex: 'pincode',
      key: 'pincode',
      render: (text, record, index) => getRowSpan(text, index, filteredShops),
      width: 100,
      align: 'center',
      onCell: (_, index) => {
        // This is a bit tricky with Ant Design Table onCell + render for rowspan.
        // The render function handles the content and props.rowSpan.
        // But onCell is usually used for props.
        // Let's rely on the render function returning an object with props.
        return {};
      }
    },
    {
      title: 'Image',
      dataIndex: 'boardImageUrl',
      key: 'boardImageUrl',
      render: (text) => {
        if (!text) return 'No Image';
        const src = text.startsWith('http') ? text : `${API_URL}${text}`;
        return <img src={src} alt="shop board" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }} />;
      },
    },
    {
      title: 'Shop Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'shopType',
      key: 'shopType',
      render: (text) => <Tag color="blue">{text || 'General Store'}</Tag>
    },
    {
      title: 'Owner Name',
      dataIndex: 'ownerName',
      key: 'ownerName',
    },
    {
      title: 'Contact Number',
      dataIndex: 'contactNumber',
      key: 'contactNumber',
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        let color = 'default';
        if (status === 'Verified') color = 'green';
        if (status === 'Pending') color = 'orange';
        if (status === 'Rejected') color = 'red';
        
        const bdeName = record.bdeId ? (bdes.find(u => u.id === record.bdeId)?.name || record.bdeId) : null;

        return (
          <Space direction="vertical" size={0}>
            <Tag color={color}>{status || 'Verified'}</Tag>
            {bdeName && <small style={{ color: '#888' }}>By {bdeName}</small>}
          </Space>
        );
      }
    },
    {
      title: 'Action',
      key: 'action',
      align: 'right',
      render: (_, record) => {
        const hasPendingOrders = orders.some(o =>
          (o.customerName === record.name || o.customerName === record.ownerName) &&
          o.status !== 'Completed' &&
          o.status !== 'Cancelled'
        );

        return (
          <Space size="middle">
            {record.status === 'Pending' && (
              <>
                <Button
                  type="link"
                  onClick={() => handleVerify(record)}
                  style={{ color: '#52c41a', padding: 0 }}
                >
                  Verify
                </Button>
                <Button
                  type="link"
                  danger
                  onClick={() => openRejectModal(record)}
                  style={{ padding: 0 }}
                >
                  Reject
                </Button>
                <div style={{ width: 1, height: 16, background: '#f0f0f0' }} />
              </>
            )}

            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => navigate(`/admin/shops/edit/${record.id}`)}
            >
              Edit
            </Button>
            <Popconfirm
              title={hasPendingOrders ? "Cannot delete shop" : "Delete the shop"}
              description={hasPendingOrders ? "This shop has pending orders." : "Are you sure to delete this shop?"}
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
              disabled={hasPendingOrders}
            >
              <Button type="link" danger icon={<DeleteOutlined />} disabled={hasPendingOrders}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        )
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 16 }}>
        <h2>Shops Management</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Input.Search
            allowClear
            placeholder="Search shops"
            style={{ width: 320, maxWidth: '100%' }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button icon={<FileExcelOutlined />} onClick={handleExport}>
            Export to Excel
          </Button>
          <Button type="primary" icon={<ShopOutlined />} onClick={() => navigate('/admin/shops/create')}>
            Add New Shop
          </Button>
        </div>
      </div>
      {!screens.md ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {filteredShops.map(shop => {
            const hasPendingOrders = orders.some(o =>
              (o.customerName === shop.name || o.customerName === shop.ownerName) &&
              o.status !== 'Completed' &&
              o.status !== 'Cancelled'
            );

            let statusColor = 'default';
            if (shop.status === 'Verified') statusColor = 'green';
            if (shop.status === 'Pending') statusColor = 'orange';
            if (shop.status === 'Rejected') statusColor = 'red';

            const imgSrc = shop.boardImageUrl ? (shop.boardImageUrl.startsWith('http') ? shop.boardImageUrl : `${API_URL}${shop.boardImageUrl}`) : null;

            return (
              <Card key={shop.id} size="small"
                cover={imgSrc ? <img alt={shop.name} src={imgSrc} style={{ height: 150, objectFit: 'cover' }} /> : null}
                actions={[
                  <Button type="link" icon={<EditOutlined />} onClick={() => navigate(`/admin/shops/edit/${shop.id}`)}>Edit</Button>,
                  <Popconfirm
                    title={hasPendingOrders ? "Cannot delete shop" : "Delete the shop"}
                    description={hasPendingOrders ? "This shop has pending orders." : "Are you sure?"}
                    onConfirm={() => handleDelete(shop.id)}
                    okText="Yes"
                    cancelText="No"
                    disabled={hasPendingOrders}
                  >
                    <Button type="link" danger icon={<DeleteOutlined />} disabled={hasPendingOrders}>Delete</Button>
                  </Popconfirm>
                ]}
              >
                <Card.Meta
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{shop.name}</span>
                      <Tag color={statusColor}>{shop.status || 'Verified'}</Tag>
                    </div>
                  }
                  description={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Owner:</span>
                        <span style={{ color: '#1e293b', fontWeight: 500 }}>{shop.ownerName}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Mobile:</span>
                        <span style={{ color: '#1e293b', fontWeight: 500 }}>{shop.contactNumber}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Pincode:</span>
                        <span style={{ color: '#1e293b', fontWeight: 500 }}>{shop.pincode}</span>
                      </div>
                      <div style={{ fontSize: 13 }}>{shop.address}</div>
                      {shop.bdeId && <div style={{ fontSize: 12, color: '#888' }}>Added by BDE</div>}

                      {shop.status === 'Pending' && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <Button type="primary" size="small" style={{ background: '#52c41a', borderColor: '#52c41a' }} onClick={() => handleVerify(shop)}>Verify</Button>
                          <Button danger size="small" onClick={() => openRejectModal(shop)}>Reject</Button>
                        </div>
                      )}
                    </div>
                  }
                />
              </Card>
            )
          })}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={filteredShops}
          rowKey="id"
          loading={loading}
          bordered
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onDoubleClick: () => {
              navigate(`/admin/shops/view/${record.id}`);
            },
            style: { cursor: 'pointer' },
          })}
        />
      )}

      <Modal
        title="Reject Shop"
        open={isRejectModalVisible}
        onOk={handleReject}
        onCancel={() => setIsRejectModalVisible(false)}
        confirmLoading={updateLoading}
        okText="Reject"
        okButtonProps={{ danger: true }}
      >
        <p>Please provide a reason for rejecting <b>{currentShop?.name}</b>:</p>
        <Input.TextArea
          rows={4}
          value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
          placeholder="e.g., Incorrect GST number, blurry image..."
          autoFocus
        />
      </Modal>
    </div>
  );
};

export default Shops;
